import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Card, CardBody } from 'reactstrap';

import { activateAuthLayout } from '../../../store/actions';
import { connect } from 'react-redux';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { getQuoteBuilderURL } from '../../../helpers/common';
import { useHistory } from 'react-router';
/**
 * graphQL query for all the data needed for customise proposal UI
 * Quote - quote based from the passed quote id in the URL
 **/
const DATA = gql`
  query Data($QuoteId: ID) {
    Quote(QuoteId: $QuoteId) {
      QuoteId
      Description
      PrimaryContact
      QuoteTypeId1
      QuoteType1 {
        QuoteTypeId
        Name
      }
      QuoteTypeId2
      QuoteType2 {
        QuoteTypeId
        Name
      }
      WorkflowStep
      AcceptedQuote
      ModifiedTimestamp
      Status
      IsTemplate
    }
    ProposalTemplates {
      TemplateId
      Name
      TOCLine
      DefaultPosition
    }
    ProposalInserts(QuoteId: $QuoteId) {
      QuoteId
      InsertId
      TemplateId
      SortOrder
      AddedTimestamp
      ProposalTemplates {
        Name
        TOCLine
        DefaultPosition
      }
    }
  }
`;

const UPDATE_QUOTE = gql`
  mutation updateQuote($QuoteInput: QuoteInput) {
    updateQuote(input: $QuoteInput) {
      QuoteId
      CompanyName
      WorkflowStep
      AcceptedQuote
      ProposalModified
      CustomerId
    }
  }
`;

const CREATE_PROPOSAL_INSERTS = gql`
  mutation createProposalInserts($ProposalInserts: [ProposalInsertInput]) {
    createProposalInserts(input: $ProposalInserts) {
      InsertId
      QuoteId
      TemplateId
      SortOrder
      AddedTimestamp
    }
  }
`;

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

/**
 * Moves an item from one list to another list.
 */
const move = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, {
    ...removed,
    id: `p-custom-${Math.random().toString(36).substr(2, 20)}`,
  });

  const result = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

const grid = 4;

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: 'none',
  padding: grid * 2,
  margin: `0 0 ${grid}px 0`,
  display: 'flex',
  alignItems: 'center',
  background: '#fff',
  border: '1px solid #ccc',
  ...draggableStyle,
});

const getLockedItemStyle = () => ({
  background: '#3f86ff',
  color: '#fff',
  border: '1px solid transparent',
  padding: '8px',
  margin: `0 0 ${grid}px 0`,
  maxWidth: '400px',
});

const getLockIconStyle = () => ({
  color: '#fff',
  marginRight: '8px',
});

const getListStyle = (isDraggingOver, isAvailableInserts = true) => ({
  border: isAvailableInserts ? '1px solid #ccc' : 'none',
  padding: isAvailableInserts ? '4px 4px 0' : 0,
  paddingBottom: 0,
  maxWidth: '400px',
  width: '100%',
});

const QuoteBuilderCustomiseProposal = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  const history = useHistory();
  // initialize states
  const userModified = useRef(false);
  const [disableQuote, setDisableQuote] = useState(false);
  const [state, setState] = useState({
    included: [],
    defaultTemplatesStart: [],
    defaultTemplatesEnd: [],
  });

  // initialize apollo graphql queries and mutations
  const { data, loading } = useQuery(DATA, {
    variables: { QuoteId: props.quoteId },
  });
  const [updateQuote] = useMutation(UPDATE_QUOTE);
  const [createProposalInserts] = useMutation(CREATE_PROPOSAL_INSERTS);

  const listProposalInserts = useCallback(() => {
    return [...data.ProposalInserts]
      .sort((a, b) => a.SortOrder < b.SortOrder ? -1 : 1)
      .map((item, index) => {
        const proposalTemplate = item?.ProposalTemplates;
        if (!proposalTemplate) return undefined;
        return {
          ...item,
          Name: proposalTemplate.Name || '',
          id: `i-insert-${index}`,
          DefaultPosition: proposalTemplate.DefaultPosition ?? null,
        };
      })
      .filter(Boolean);
  }, [data]);

  const returnDefaultTemplates = useCallback((type = 'start') => {
    return data.ProposalTemplates.filter(
      (template) => template.DefaultPosition === type
    );
  }, [data]);

  const goToPreviousStep = (e) => {
    e.preventDefault();
    updateQuote({
      variables: {
        QuoteInput: {
          QuoteId: data.Quote.QuoteId,
          WorkflowStep: 'QUOTE_RATES',
        },
      },
    }).then(() => {
      history.replace(`/quote-rates/${data.Quote.QuoteId}`);
    });
  };

  const proceedNextStep = (e) => {
    e.preventDefault();
    const nextStep = 'SEND_PROPOSAL';
    const nextPath = data.Quote.IsTemplate
      ? `/preview-template/${data.Quote.QuoteId}`
      : `/send-proposal/${data.Quote.QuoteId}`;
    updateQuote({
      variables: {
        QuoteInput: {
          QuoteId: data.Quote.QuoteId,
          WorkflowStep: nextStep,
        },
      },
    }).then(() => {
      history.replace(nextPath);
    });
  };

  /**
   * A semi-generic way to handle multiple lists. Matches
   * the IDs of the droppable container to the names of the
   * source arrays stored in the state.
   */
  const available = data?.ProposalTemplates
    ? data.ProposalTemplates.filter(
        (template) =>
          template.DefaultPosition !== 'start' &&
          template.DefaultPosition !== 'end' &&
          !state.included.some(
            (i) => parseInt(i.TemplateId) === parseInt(template.TemplateId)
          )
      ).map((item, index) => ({ ...item, id: `p-custom-${index}` }))
    : [];

  const id2List = {
    included: 'included',
    available: 'available',
  };

  const getList = (id) => (id === 'available' ? available : state[id2List[id]]);

  const onDragEnd = (result) => {
    const { source, destination } = result;
    const draggedItem = getList(source.droppableId)[source.index];
    const isDraggedItemExist =
      state.included.filter(
        (item) =>
          parseInt(item.TemplateId) ===
          parseInt(draggedItem.TemplateId)
      ).length > 0
        ? true
        : false;

    // dropped outside the list
    if (!destination) {
      return;
    }

    // if the template item already exists - don't add the item
    if (isDraggedItemExist && source.droppableId !== destination.droppableId) {
      return;
    }

    userModified.current = true;

    if (source.droppableId === destination.droppableId) {
      const items = reorder(
        getList(source.droppableId),
        source.index,
        destination.index
      );

      setState({
        ...state,
        included: items,
      });
    } else {
      const result = move(
        getList(source.droppableId),
        getList(destination.droppableId),
        source,
        destination
      );

      setState({
        ...state,
        included: result.included,
      });
    }
  };

  const deleteInsert = (item) => {
    userModified.current = true;
    let inserts = [...state.included];
    const index = state.included.map((insert) => insert.id).indexOf(item.id);
    inserts.splice(index, 1);
    setState({ ...state, included: inserts });
  };

  useEffect(() => {
    if (!userModified.current || !data) return;
    const inserts = state.included.map((item, index) => ({
      QuoteId: parseInt(data.Quote.QuoteId, 10),
      SortOrder: index,
      TemplateId: parseInt(item.TemplateId),
    }));
    createProposalInserts({
      variables: { ProposalInserts: inserts },
      refetchQueries: ['getCurrentQuote']
    });
  }, [state.included, data, createProposalInserts]);

  useEffect(() => {
    if (data && data.Quote) {
      setState(s => ({
        ...s,
        defaultTemplatesStart: returnDefaultTemplates('start'),
        defaultTemplatesEnd: [
          ...returnDefaultTemplates('end'),
          {
            TemplateId: null,
            TOCLine: 'Signature',
          },
        ],
        included: listProposalInserts(),
      }));
      // disable screen when status is accepted, declined or voided
      const status = data.Quote.Status;
      setDisableQuote(status === 'accepted');

      if (!['CUSTOMISE_PROPOSAL', 'CUSTOMER_ACCEPTED'].includes(data.Quote.WorkflowStep)) {
        history.replace(getQuoteBuilderURL(data.Quote.WorkflowStep, data.Quote.QuoteId));
        return;
      }
    }
  }, [data, history, listProposalInserts, returnDefaultTemplates]);

  return (
    <React.Fragment>
      {disableQuote && <div className="overlay"></div>}
      {
        // load live search and quote item builder UI when quote with the ID passed on the URL exists
        data && data.Quote && !loading && (
          <div className="content">
            <div className="container-fluid">
              <div className="page-title-box">
                <Row className="align-items-center">
                  <Col sm="6">
                    <h4 className="page-title">{data.Quote.Description} - Customise Proposal</h4>
                  </Col>
                </Row>
              </div>
              <h6>CUSTOMISE PROPOSAL</h6>

              <Card>
                <CardBody>
                  <Row>
                    <Col lg={{ size: 8, offset: 2 }}>
                      <DragDropContext onDragEnd={onDragEnd}>
                        <Row className="mb-4">
                          <Col md="6">
                            <h5>Proposal Inserts</h5>
                            {state &&
                              state.defaultTemplatesStart &&
                              state.defaultTemplatesStart.map(
                                (template, index) => (
                                  <div key={index} style={getLockedItemStyle()}>
                                    <i
                                      className="fas fa-lock"
                                      style={getLockIconStyle()}
                                    ></i>
                                    {template.TOCLine}
                                  </div>
                                )
                              )}
                            <Droppable droppableId="included">
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  style={getListStyle(
                                    snapshot.isDraggingOver,
                                    false
                                  )}
                                >
                                  {state &&
                                    state.included &&
                                    state.included.map((item, index) => (
                                      <Draggable
                                        key={item.id}
                                        draggableId={item.id}
                                        index={index}
                                      >
                                        {(provided, snapshot) => (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={getItemStyle(
                                              snapshot.isDragging,
                                              provided.draggableProps.style,
                                            )}
                                          >
                                            <i className="fas fa-grip-vertical mr-2" style={{ color: '#bbb' }} />
                                            <span className="proposal-insert-name">
                                              {item.Name}
                                            </span>
                                            {item.Name && (
                                              <i
                                                className="fas fa-trash-can proposal-insert-actions"
                                                style={{ cursor: 'pointer', color: '#bbb' }}
                                                onClick={() => deleteInsert(item)}
                                              />
                                            )}
                                          </div>
                                        )}
                                      </Draggable>
                                    ))}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                            {state &&
                              state.defaultTemplatesEnd &&
                              state.defaultTemplatesEnd.map(
                                (template, index) => (
                                  <div key={index} style={getLockedItemStyle()}>
                                    <i
                                      className="fas fa-lock"
                                      style={getLockIconStyle()}
                                    ></i>
                                    {template.TOCLine}
                                  </div>
                                )
                              )}
                          </Col>
                          <Col md="6">
                            <h5>Available Inserts</h5>
                            <Droppable
                              isDropDisabled={true}
                              droppableId="available"
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  style={getListStyle(snapshot.isDraggingOver)}
                                >
                                  {available.map((item, index) => (
                                      <Draggable
                                        key={item.id}
                                        draggableId={item.id}
                                        index={index}
                                      >
                                        {(provided, snapshot) => (
                                          <>
                                            <div
                                              ref={provided.innerRef}
                                              {...provided.draggableProps}
                                              {...provided.dragHandleProps}
                                              style={getItemStyle(
                                                snapshot.isDragging,
                                                provided.draggableProps.style
                                              )}
                                            >
                                              <i className="fas fa-grip-vertical mr-2" style={{ color: '#bbb' }} />
                                              <span className="proposal-insert-name">{item.Name}</span>
                                            </div>
                                            {snapshot.isDragging && (
                                              <div style={getItemStyle()}>
                                                <i className="fas fa-grip-vertical mr-2" style={{ color: '#bbb' }} />
                                                <span className="proposal-insert-name">{item.Name}</span>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </Draggable>
                                    ))}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          </Col>
                        </Row>
                      </DragDropContext>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      <button
                        className="btn-icon btn btn-light btn-lg float-left"
                        onClick={goToPreviousStep}
                      >
                        {' '}
                        <span className="btn-icon-label">
                          <i className="ion ion-md-arrow-back mr-2"></i>
                        </span>{' '}
                        Previous Step
                      </button>
                      <button
                        className="btn-icon btn btn-primary btn-lg float-right"
                        onClick={proceedNextStep}
                      >
                        {' '}
                        <span className="btn-icon-label">
                          <i className="ion ion-md-arrow-forward mr-2"></i>
                        </span>{' '}
                        Next Step
                      </button>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )
      }
      {
        // when data is loaded and quote id is not valid show quote does not exists error UI
        data && data.Quote === null && !loading && (
          <div className="content">
            <div className="container-fluid">
              <div className="page-title-box">
                <Row className="align-items-center">
                  <Col sm="6">
                    <h4 className="page-title">Error loading this page</h4>
                  </Col>
                </Row>
              </div>

              <Row>
                <Col lg="12">
                  <Card>
                    <CardBody>
                      Quote with id {props.quoteId} does not exist.
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
        )
      }
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(
  QuoteBuilderCustomiseProposal
);
